import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { ICFormikDropDown } from "../../core-component/formik";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
  Loader,
} from "../../core-component/ui";
import { IOption } from "../../data/AppInterface";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { cn } from "../../lib";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  escapeHtml,
  hasAnyModification,
  prepareMessageFromParams,
} from "../../utils/AppFunctions";
import {
  CREDIT_TYPE_LABEL_KEY,
  getPlanTypeLabel,
  SUBSCRIPTION_SOURCE_LABEL_KEY,
} from "../../utils/PurchaseConstants";
import { ASSIGN_RESULT_ENUM } from "../../utils/PurchaseEnumerations";
import ValueChange from "../../components/purchase/ValueChange";
import {
  formatAmountWithSign,
  formatCredits,
  formatPurchaseDate,
} from "../../utils/PurchaseFormat";
import { getFirmNameDdl } from "./FirmPurchasedSubscriptionViewController";
import {
  assignSubscription,
  fetchAssignableSubscriptionPackage,
  fetchAssignmentPreview,
  fetchFirmSubscriptionDetail,
  FIRM_SUBSCRIPTION_ASSIGNMENT_INITIAL_VALUES,
  firmSubscriptionAssignmentValidationSchema,
  getIncludedCreditLabel,
  getPackageOptions,
  IAssignableSubscriptionPackage,
  IAssignmentPreview,
  IFirmSubscriptionAssignmentForm,
  IFirmSubscriptionDetail,
} from "./FirmSubscriptionAssignmentFormController";

interface IFirmSubscriptionAssignmentFormProps {
  handleClose: (status: boolean) => void;
}

interface IPriceCellProps {
  label: string;
  value: string;
}

const PriceCell: React.FC<IPriceCellProps> = ({ label, value }) => (
  <div>
    <div className="text-xs text-muted">{label}</div>
    <div>{value}</div>
  </div>
);

const FirmSubscriptionAssignmentForm: React.FC<IFirmSubscriptionAssignmentFormProps> = ({
  handleClose,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [firmOptions, setFirmOptions] = useState<IOption<string>[]>([]);
  const [packages, setPackages] = useState<IAssignableSubscriptionPackage[]>([]);
  const [firmDetail, setFirmDetail] = useState<IFirmSubscriptionDetail>();
  const [preview, setPreview] = useState<IAssignmentPreview>();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [valuesToConfirm, setValuesToConfirm] = useState<IFirmSubscriptionAssignmentForm>();
  const previewRequest = useRef(0);
  const firmRequest = useRef(0);

  useEffect(() => {
    Promise.all([
      getFirmNameDdl(setFirmOptions, false),
      fetchAssignableSubscriptionPackage(setPackages),
    ]).finally(() => setLoading(false));
  }, []);

  const loadPreview = async (firmId: string, packageId: string) => {
    previewRequest.current += 1;
    const request = previewRequest.current;
    setPreview(undefined);
    if (!firmId || !packageId) {
      setPreviewLoading(false);
      return undefined;
    }
    setPreviewLoading(true);
    const result = await fetchAssignmentPreview(firmId, packageId);
    if (request !== previewRequest.current) return undefined;
    setPreview(result);
    setPreviewLoading(false);
    return result;
  };

  const handleFirmChange = async (firmId: string, packageId: string) => {
    firmRequest.current += 1;
    const request = firmRequest.current;
    const detail = await fetchFirmSubscriptionDetail(firmId);
    if (request !== firmRequest.current) return;
    setFirmDetail(detail);
    loadPreview(firmId, packageId);
  };

  const submit = async (values: IFirmSubscriptionAssignmentForm, confirmReplace: boolean) => {
    const result = await assignSubscription(values, confirmReplace, setSubmitLoading);
    if (result === ASSIGN_RESULT_ENUM.Assigned) {
      handleClose(true);
    } else if (result === ASSIGN_RESULT_ENUM.NeedsConfirmation) {
      await loadPreview(values.firm_id, values.subscription_package_id);
      setValuesToConfirm(values);
    }
  };

  const handleSubmit = (values: IFirmSubscriptionAssignmentForm) => {
    if (preview?.replaces) {
      setValuesToConfirm(values);
      return;
    }
    submit(values, false);
  };

  const checkFormModification = (values: IFirmSubscriptionAssignmentForm) => {
    if (hasAnyModification(values, FIRM_SUBSCRIPTION_ASSIGNMENT_INITIAL_VALUES)) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  const detailRow = (label: string, value: string) => (
    <div className="flex items-center mt-1" key={label}>
      <div className="text-sm text-muted">{label} :</div>
      <div className="ml-2">{value}</div>
    </div>
  );

  const latestSubscriptionCard = () => {
    if (!firmDetail) return null;
    const subscription = firmDetail.subscription;
    return (
      <div className="bg-base rounded-md p-4">
        <div className="font-semibold">{t("latest_subscription")}</div>
        {subscription ? (
          <>
            {detailRow(t("package"), subscription.subscription_package_name)}
            {detailRow(t("source"), t(SUBSCRIPTION_SOURCE_LABEL_KEY[subscription.source]))}
            {detailRow(t("end_date"), formatPurchaseDate(subscription.end_date))}
            {detailRow(
              t("expiry_days"),
              subscription.is_expired === BIT_VALUE.One
                ? t("subscription_expired")
                : String(subscription.remaining_days),
            )}
          </>
        ) : (
          <div className="text-sm text-muted mt-2">{t("no_subscription_found")}</div>
        )}
      </div>
    );
  };

  const packageCard = (packageId: string) => {
    const selected = packages.find((item) => item.id === packageId);
    if (!selected) return null;
    return (
      <div className="bg-base rounded-md p-4 space-y-3">
        <div className="flex items-end justify-between">
          <div className="text-2xl font-semibold">{formatAmountWithSign(selected.total)}</div>
          <div className="text-sm text-muted">
            {prepareMessageFromParams(t("validity_days_value"), [["days", String(selected.validity)]])}
          </div>
        </div>
        <div className="text-sm text-muted">{getPlanTypeLabel(t, selected.platforms)}</div>
        <div className="grid grid-cols-4 gap-2 border-t pt-2">
          <PriceCell label={t("base_price")} value={formatAmountWithSign(selected.base_price)} />
          <PriceCell label={t("cgst")} value={`${Number(selected.cgst)}%`} />
          <PriceCell label={t("sgst")} value={`${Number(selected.sgst)}%`} />
          <PriceCell label={t("igst")} value={`${Number(selected.igst)}%`} />
        </div>
        <div className="text-xs text-muted font-semibold">{t("credits_it_adds")}</div>
        {previewLoading ? (
          <div className="text-sm text-muted">{t("loading")}</div>
        ) : (
          preview && (
            <div className="grid grid-cols-4 gap-2">
              {preview.credits.map((credit) => (
                <div key={credit.credit_type}>
                  <div className="text-xs text-muted">{t(CREDIT_TYPE_LABEL_KEY[credit.credit_type])}</div>
                  <div className={cn({ "text-danger font-semibold": credit.over_cap })}>
                    <ValueChange
                      from={formatCredits(t, credit.balance_before)}
                      to={formatCredits(t, credit.balance_after)}
                    />
                  </div>
                  <div className="text-xs text-muted">{getIncludedCreditLabel(t, credit)}</div>
                </div>
              ))}
            </div>
          )
        )}
        <div className="text-xs text-muted">{t("assignment_no_documents")}</div>
        {preview?.over_cap_message && (
          <div className="rounded-md p-2 bg-danger-muted text-danger text-sm">
            {preview.over_cap_message}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <Loader />;

  return (
    <>
      <Formik
        initialValues={FIRM_SUBSCRIPTION_ASSIGNMENT_INITIAL_VALUES}
        validationSchema={firmSubscriptionAssignmentValidationSchema(t)}
        onSubmit={handleSubmit}
      >
        {(formikProps) => {
          const { values } = formikProps;
          const isBlocked = !!preview?.over_cap_message || previewLoading;
          return (
            <FormRightModalWrapper
              containerClassName="!w-1/3"
              onClose={() => checkFormModification(values)}
              title={t("assign_subscription")}
            >
              <Form onSubmit={formikProps.handleSubmit} className="flex flex-col h-full">
                <div className="flex-1 space-y-4">
                  <ICFormikDropDown
                    {...formikProps}
                    name="firm_id"
                    placeholder={t("firm_name")}
                    options={firmOptions}
                    searchable
                    required
                    onChange={(firmId: string) => handleFirmChange(firmId, values.subscription_package_id)}
                  />
                  {latestSubscriptionCard()}
                  <ICFormikDropDown
                    {...formikProps}
                    name="subscription_package_id"
                    placeholder={t("subscription_package")}
                    options={getPackageOptions(t, packages)}
                    searchable
                    required
                    onChange={(packageId: string) => loadPreview(values.firm_id, packageId)}
                  />
                  {packageCard(values.subscription_package_id)}
                </div>
                <ICButton
                  loading={submitLoading}
                  disabled={isBlocked || submitLoading}
                  className={cn("w-full", { "opacity-50 cursor-not-allowed": isBlocked })}
                >
                  {t("submit")}
                </ICButton>
              </Form>
            </FormRightModalWrapper>
          );
        }}
      </Formik>
      {valuesToConfirm && preview?.replaces && (
        <ICConfirmationModal
          title={t("replace_running_package")}
          message={prepareMessageFromParams(t("replace_running_package_message"), [
            ["current", escapeHtml(preview.replaces.package_name)],
            ["firm", escapeHtml(firmDetail?.firm_name ?? "")],
            ["days", String(preview.replaces.remaining_days)],
            ["new", escapeHtml(preview.package_name)],
          ])}
          onClose={(confirmed) => {
            const values = valuesToConfirm;
            setValuesToConfirm(undefined);
            if (confirmed) submit(values, true);
          }}
        />
      )}
      {showDiscardWarning && (
        <ICConfirmationModal
          title={t("discard_warning")}
          message={t("discard_unsaved_changes")}
          onClose={(value) => {
            setShowDiscardWarning(false);
            value && handleClose(false);
          }}
        />
      )}
    </>
  );
};

export default FirmSubscriptionAssignmentForm;
